'use client';

import type { ReactNode } from 'react';

import PageBuilder from '@commons/pagebuilder';
import type { WebformProps } from '@commons/pagebuilder/webforms';
import WebformDrawer, { type WebformDrawerProps } from '@commons/pagebuilder/webforms/Drawer';
import { API_WEBSITE } from '@commons/utils/constants';

/**
 * Helper function to get the webform id based on the website.
 *
 * @returns The webform id or undefined if not found.
 */

const getWebformId = () => {
  switch (API_WEBSITE) {
    case 'crisis24':
      return 'gated_insight';
    case 'codered':
      return 'codered_gated_insight';
  }
};

type Props = Pick<WebformDrawerProps, 'trigger' | 'triggerProps'> &
  Pick<WebformProps, 'resourceLabel' | 'submitLabel'> & {
    fallback?: ReactNode;
    url: string;
    /**
     * Open the resource URL in a new tab after a successful form submission.
     */
    openURLOnSuccess?: boolean;
    /*
     * Sync the webform id with the URL query parameter
     * to open the drawer when the page is loaded with the webform id in the URL.
     */
    syncFormQueryParam?: boolean;
  };

/**
 * Renders the active website's gated insight form in a drawer.
 */

const GatedInsightDrawer = ({
  url,
  fallback,
  openURLOnSuccess,
  resourceLabel,
  syncFormQueryParam,
  submitLabel,
  trigger,
  triggerProps,
}: Props) => {
  const webformId = getWebformId();

  if (!webformId) return fallback;

  const handleSuccess = openURLOnSuccess
    ? () => {
        setTimeout(() => {
          window.open(url, '_blank', 'noopener,noreferrer');
        }, 2000);
      }
    : undefined;

  const renderedWebform = PageBuilder.renderWebform(webformId, {
    url,
    inDrawer: true,
    onSuccess: handleSuccess,
    resourceLabel,
    submitLabel,
  });

  return (
    <WebformDrawer
      webformId={syncFormQueryParam ? webformId : undefined}
      trigger={trigger}
      triggerProps={triggerProps}
    >
      {renderedWebform}
    </WebformDrawer>
  );
};

export default GatedInsightDrawer;
